import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Upload, FileText } from 'lucide-react';

export function SupplierMyDetails({ token, PORTAL_API }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const authH = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    api.get(`${PORTAL_API}/my-details`, authH)
      .then(r => { setDetails(r.data); setForm(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${PORTAL_API}/my-details`, {
        address: form.address, city: form.city, state_province: form.state_province,
        zip_postal: form.zip_postal, country: form.country, website: form.website,
        contacts: form.contacts, category_codes: form.category_codes,
        sic_code: form.sic_code, sic_description: form.sic_description,
        duns_number: form.duns_number, registration_number: form.registration_number,
        compliance: form.compliance,
      }, authH);
      toast.success('Details updated');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const handleDocUpload = async (file) => {
    const docType = window.prompt('Document type (e.g. Insurance, Certification, License):') || 'General';
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      await api.post(`${PORTAL_API}/my-details/documents`, fd, { headers: { ...authH.headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      const r = await api.get(`${PORTAL_API}/my-details`, authH);
      setDetails(r.data); setForm(r.data);
    } catch { toast.error('Upload failed'); }
  };

  if (loading) return <p className="text-sm text-slate-400 text-center py-8">Loading...</p>;
  if (!details) return <p className="text-sm text-slate-400 text-center py-8">Unable to load details.</p>;

  const diversity = form.compliance?.diversity || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">My Details</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Company Info */}
      <div className="border rounded-xl p-5 bg-white mb-4">
        <h3 className="text-sm font-semibold mb-3">Company Information</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><label className="text-xs text-slate-500">Company Name</label><p className="font-semibold">{details.supplier_name || details.company_name}</p></div>
          <div><label className="text-xs text-slate-500">Supplier Code</label><p className="font-mono">{details.supplier_code || ''}</p></div>
        </div>
      </div>

      {/* Address (editable) */}
      <div className="border rounded-xl p-5 bg-white mb-4">
        <h3 className="text-sm font-semibold mb-3">Address</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><label className="text-xs text-slate-500">Street Address</label><input value={form.address || ''} onChange={e => f('address', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">City</label><input value={form.city || ''} onChange={e => f('city', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">State / Province</label><input value={form.state_province || ''} onChange={e => f('state_province', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">Postal Code</label><input value={form.zip_postal || ''} onChange={e => f('zip_postal', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">Country</label><input value={form.country || ''} onChange={e => f('country', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">Website</label><input value={form.website || ''} onChange={e => f('website', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
        </div>
      </div>

      {/* Identifiers */}
      <div className="border rounded-xl p-5 bg-white mb-4">
        <h3 className="text-sm font-semibold mb-3">Business Identifiers</h3>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><label className="text-xs text-slate-500">SIC Code</label><input value={form.sic_code || ''} onChange={e => f('sic_code', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">SIC Description</label><input value={form.sic_description || ''} onChange={e => f('sic_description', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">D-U-N-S Number</label><input value={form.duns_number || ''} onChange={e => f('duns_number', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
          <div><label className="text-xs text-slate-500">Tax / Registration Number</label><input value={form.registration_number || ''} onChange={e => f('registration_number', e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
        </div>
      </div>

      {/* Diversity */}
      <div className="border rounded-xl p-5 bg-white mb-4">
        <h3 className="text-sm font-semibold mb-3">Supplier Diversity</h3>
        <div className="flex flex-wrap gap-4">
          {[
            { key: 'mbe', label: 'Minority-Owned (MBE)' },
            { key: 'wbe', label: 'Woman-Owned (WBE)' },
            { key: 'vosb', label: 'Veteran-Owned (VOSB)' },
            { key: 'sdvosb', label: 'Service-Disabled Veteran (SDVOSB)' },
            { key: 'hubzone', label: 'HUBZone' },
            { key: 'sdb', label: 'Small Disadvantaged (SDB)' },
            { key: 'lgbtbe', label: 'LGBT-Owned (LGBTBE)' },
            { key: 'dobe', label: 'Disability-Owned (DOBE)' },
          ].map(d => (
            <label key={d.key} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={diversity[d.key] || false}
                onChange={e => setForm(prev => ({ ...prev, compliance: { ...prev.compliance, diversity: { ...diversity, [d.key]: e.target.checked } } }))}
                className="h-4 w-4" />
              {d.label}
            </label>
          ))}
        </div>
      </div>

      {/* Documents */}
      <div className="border rounded-xl p-5 bg-white mb-4">
        <h3 className="text-sm font-semibold mb-3">Documents</h3>
        <div
          onDrop={e => { e.preventDefault(); const file = e.dataTransfer.files?.[0]; if (file) handleDocUpload(file); }}
          onDragOver={e => e.preventDefault()}
          onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.onchange = ev => { const file = ev.target.files?.[0]; if (file) handleDocUpload(file); }; input.click(); }}
          className="border-2 border-dashed rounded-lg p-4 text-center text-xs text-slate-400 hover:border-indigo-400 hover:bg-slate-50 cursor-pointer mb-3"
        >
          <Upload className="w-5 h-5 mx-auto mb-1" /> Drag & drop or click to upload (Insurance, Certifications, etc.)
        </div>
        {(details.documents || []).length > 0 && (
          <div className="space-y-1">
            {details.documents.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded">
                <FileText className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-medium">{d.file_name}</span>
                <Badge className="text-[9px] bg-slate-200 text-slate-600">{d.doc_type || 'General'}</Badge>
                <span className="text-slate-400 ml-auto">{d.uploaded_by}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
